import { useRef, useEffect, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import usStates from '../../../constants/Map/polygons/us-states.json'
import { stateToRegionByName } from '../../../constants/Map';
import { handleClick, handleMoveEnd, handleZoom, handleHover, resetWellMapHoverState } from '../../../components/MapSection/features/wellWaterSystem';
import { mapColorPalette } from "../../../constants/Map";
import { Dispatch } from 'redux';
import { useError } from '../../../context/ErrorContext';
import { WaterSystemType } from '../../../store/waterSystems';

interface MapInitializationProps {
    regionsHousingUnitsRef: React.MutableRefObject<any>;
    prevSelectedCode: React.MutableRefObject<string | null>;
    prevSelectedCounty: React.MutableRefObject<string | null>;
    prevSelectedGeoblock: React.MutableRefObject<string | null>;
    countyHousingUnitsRef: React.MutableRefObject<any>;
    geoBlocksGroupUnitsRef: React.MutableRefObject<any>;
    wellWaterSystemMapMetricsRef: React.MutableRefObject<any>;
    statesRef: React.MutableRefObject<any>,
    selectedStateRef: React.MutableRefObject<any>,
    markersRef: React.MutableRefObject<any[]>;
    dispatch: Dispatch;
    waterSystemType: WaterSystemType;
  }
  
  export const useWellMapInitialization = ({
    regionsHousingUnitsRef,
    prevSelectedCode,
    prevSelectedCounty,
    prevSelectedGeoblock,
    countyHousingUnitsRef,
    geoBlocksGroupUnitsRef,
    wellWaterSystemMapMetricsRef,
    statesRef,
    selectedStateRef,
    markersRef,
    dispatch,
    waterSystemType
  }: MapInitializationProps) => {
    const { setError } = useError();
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const hasDispatchedZoomOut = useRef<boolean>(false);
    const mapBoxAccessToken =
    (window.__ENV__ &&
      window.__ENV__?.MAPBOX_ACCESS_KEY !== '__MAPBOX_ACCESS_KEY__' &&
      window.__ENV__?.MAPBOX_ACCESS_KEY) ||
    process.env.REACT_APP_MAPBOX_ACCESS_KEY ||
    ''
  
    const initializeMapSources = useCallback(() => {
      if (!mapRef.current) return;
      
      mapRef.current.addSource('main', {
        type: 'geojson',
        data: usStates as any,
      });
    }, []);
  
    const initializeMapLayers = useCallback((regionToColorMapping: Record<string, string>) => {
      if (!mapRef.current) return;
  
      mapRef.current.addLayer({
        id: 'main',
        type: 'fill',
        source: 'main',
        layout: {},
        paint: {
          'fill-color': [
            'match',
            ['get', 'name'],
            ...Object.entries(stateToRegionByName)
              .map(([stateName, { epaRegion }]) => {
                const color = regionToColorMapping[epaRegion] || '#ffffff';
                return [stateName, color];
              })
              .flat(),
            '#ffffff',
          ],
          'fill-opacity': 0.5,
        },
      });
  
      mapRef.current.addLayer({
        id: 'outline',
        type: 'line',
        source: 'main',
        layout: {},
        paint: {
          'line-color': '#000',
          'line-width': 1,
        },
      });
    }, []);
  
    const initializeMapEvents = useCallback(() => {
      if (!mapRef.current) return;
  
      let prevZoom = mapRef.current.getZoom();
      let currentPopup: mapboxgl.Popup | null = null;
  
      mapRef.current.on('zoom', () => {
        prevZoom = mapRef.current?.getZoom() || 0;
        handleZoom({
          mapRef,
          prevZoom,
          prevSelectedCode,
          prevSelectedCounty,
          regionsHousingUnitsRef,
          countyHousingUnitsRef,
          geoBlocksGroupUnitsRef,
        });
      });

      mapRef.current.on('mousemove', 'main', (e) => {
        mapRef.current!.getCanvas().style.cursor = 'pointer';
        
        if (currentPopup) {
          currentPopup.remove();
        }

        currentPopup = handleHover(
          e, 
          mapRef, 
          //regionsHousingUnitsRef,
          countyHousingUnitsRef,
          //geoBlocksGroupUnitsRef,
          wellWaterSystemMapMetricsRef,
          dispatch
        );
      });
      
      mapRef.current.on('mouseleave', 'main', () => {
        mapRef.current!.getCanvas().style.cursor = '';
        if (currentPopup) {
          currentPopup.remove();
          currentPopup = null;
        }
        resetWellMapHoverState();
      });
      
      mapRef.current.on('moveend', () =>
        handleMoveEnd({
          mapRef,
          prevSelectedCounty,
          prevSelectedCode,
          prevSelectedGeoblock,
          markersRef,
          dispatch,
          countyHousingUnitsRef,
          hasDispatchedZoomOut,
        })
      );
      
      mapRef.current.on('click', 'main', (e) =>
        handleClick(e, mapRef, statesRef, dispatch),
      );
    }, [
      countyHousingUnitsRef, geoBlocksGroupUnitsRef, regionsHousingUnitsRef, wellWaterSystemMapMetricsRef,
      dispatch, prevSelectedCode, prevSelectedCounty, prevSelectedGeoblock, statesRef, markersRef]);
  
    const initializeMap = useCallback(() => {
      if (!mapRef.current) return;
  
      const sortedRegions = Array.isArray(regionsHousingUnitsRef.current)
        ? [...regionsHousingUnitsRef.current].sort((a, b) => b.totalHousingUnits - a.totalHousingUnits)
        : [];
  
      const regionToColorMapping = sortedRegions.reduce((acc, region, index) => {
        acc[region?.epaRegion] = mapColorPalette[index];
        return acc;
      }, {} as Record<string, string>);
  
      initializeMapSources();
      initializeMapLayers(regionToColorMapping);
      initializeMapEvents();
    }, [initializeMapSources, initializeMapLayers, initializeMapEvents]);
  
    useEffect(() => {
      if (!mapBoxAccessToken) {
        setError(new Error('Missing Mapbox API token in environment variables'));
        return;
      }
  
      mapboxgl.accessToken = mapBoxAccessToken;
  
      if (mapContainerRef.current && !mapRef.current) {
        mapRef.current = new mapboxgl.Map({
          container: mapContainerRef.current,
          style: 'mapbox://styles/mapbox/light-v11',
          center: [-98.5795, 39.8283],
          zoom: 4,
        });
  
        mapRef.current.once('load', initializeMap);
      }
  
      return () => {
        if (mapRef.current) {
          mapRef.current.remove();
          mapRef.current = null;
        }
      };
    }, [initializeMap, waterSystemType, regionsHousingUnitsRef]);
  
    return { mapRef, mapContainerRef };
  };
